import axios from "axios";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Step, Stepper } from 'react-form-stepper';
import { Helmet } from 'react-helmet';
import { FaCalendarAlt, FaTimes } from "react-icons/fa";
import { useParams } from "react-router-dom";
import arrow from "../../src/images/progress/arrow.svg";
import car from "../../src/images/progress/car.png";
import Navbar from "../components/navbar";
import Footer from "../homeComponents/footer";
import Loader from "../loader";
import Loading from "../loading";

function TrackProgress() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [dealerType, setDealerType] = useState("");
  const [serviceData, setServiceData] = useState({});
  const [SensorCode, setSensorCode] = useState("");
  const { serviceId } = useParams();
  const [serviceDetails, setServiceDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [address, setAddress] = useState(null);
  const [isEditable, setIsEditable] = useState(false);
  const [isTimeEditable, setIsTimeEditable] = useState(false);
  const [vinNumber, setVinNumber] = useState("");
  const [status, setStatus] = useState("");
  const [isChecked, setIsChecked] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  const handleChange = (e) => {
    setVinNumber(e.target.value);
  };

  useEffect(() => {
    const fetchServiceDetails = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/services/${serviceId}/get`);
        const service = response.data;

        setServiceDetails(service);
        if (service?.allottedVin) {
          setVinNumber(service.allottedVin);
        }
        if (service?.status) {
          setStatus(service.status);
          if (service.status === "Vehicle Received") {
            setIsChecked(true); // Automatically check the checkbox if status is already set
          }
        }
        if (service?.deliveryDate) {
          setSelectedDate(service.deliveryDate);
        }
  
        if (service?.dealerType) {
            setDealerType(service?.dealerType);
        }
  
        if (service?.address) {
          setAddress(service.address);
        }

        // Check if the delivery date is within 24 hours to set editability
        const deliveryDate = new Date(service?.deliveryDate);
        const currentDate = new Date();
        const timeDiff = deliveryDate - currentDate;
        if (timeDiff > 0 && timeDiff <= 24 * 60 * 60 * 1000) {
          setIsTimeEditable(true); // Allow editing if within 24 hours
        }        
      } catch (err) {
        setError("Error fetching service details.");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (serviceId) {
      fetchServiceDetails();
    }
  }, [serviceId]);

  const handleSaveVIN = async () => {
    try {
      const response = await axios.patch(
        `${API_URL}/api/services/${serviceId}/edit-vin`,
        { vinNumber }
      );
      alert(response.data.message);
      setIsEditable(false); // Disable edit mode after saving
    } catch (error) {
      console.error("Error saving VIN:", error);
      alert(error.response?.data?.message || "Error saving VIN.");
    }
  };  

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const handleConfirmClick = async (e) => {
    e.preventDefault(); // Prevent the page from refreshing
  
    if (!isChecked) return; // Ensure the checkbox is checked before proceeding
    
    setLoading(true);
  
    try {
      const payload = {
        status: "Vehicle Received",  // Update the status to 'Vehicle Received'
      };
  
      const response = await axios.patch(`${API_URL}/api/services/${serviceId}/status`, payload);
  
      // Update status in the UI
      setStatus(response.data.service.status);
      setIsChecked(true); // Keep checkbox checked after confirmation
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setLoading(false);
    }
  };  

  const handleSave = async () => {
    if (isTimeEditable) {
      try {
        const payload = { deliveryDate: selectedDate, dealerType, address };
        const response = await axios.patch(`${API_URL}/api/services/${serviceId}/update`, payload);
        alert(response.data.message);
      } catch (err) {
        console.error("Error saving changes:", err);
      }
    }
  };

  // const handleGeneratePDIReport = async () => {
  //   try {
  //     const response = await axios.patch(`${API_URL}/api/pdi-reports/regeneratePDIReport/${serviceDetails.pdiReportId}`);
      
  //     if (response.data.message) {
  //       alert(response.data.message); // Display success message
  //     }
  //   } catch (error) {
  //     console.error('Error generating PDI Report:', error);
  //     alert('Error generating PDI Report');
  //   }
  // };

  const handleGeneratePDIReport = async () => {
    try {
      let pdfUrl;

      // Call API to regenerate or get the PDF URL
      const response = await axios.patch(`${API_URL}/api/pdi-reports/regeneratePDIReport/${serviceDetails.pdiReportId}`);
      if (response.data.pdfUrl) {
        pdfUrl = response.data.pdfUrl;
      } else {
        alert(response.data.message || "Unable to open PDI Report.");
        return;
      }

      // Function to check if the URL is still valid
      const isUrlExpired = async (url) => {
        try {
          const res = await fetch(url, { method: "HEAD" });
          return !res.ok;
        } catch {
          return true;
        }
      };

      // If URL is invalid, try regenerating (your backend must handle this logic)
      if (await isUrlExpired(pdfUrl)) {
        const regenResponse = await axios.patch(`${API_URL}/api/pdi-reports/regeneratePDIReport/${serviceDetails.pdiReportId}`);
        if (regenResponse.data.pdfUrl) {
          pdfUrl = regenResponse.data.pdfUrl;
        } else {
          alert("Unable to fetch a valid PDF link.");
          return;
        }
      }

      // Force download using an <a> tag
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.setAttribute("download", `PDI_Report_${serviceDetails.pdiReportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert("PDI Report downloaded successfully!");

    } catch (error) {
      console.error("Error generating PDI Report:", error);
      alert("Something went wrong while downloading the report.");
    }
  };

  if (loading) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center bg-banner h-screen text-red-500 font-semibold">
        {error}
      </div>
    );
  }

  return (
    <div>
            <Helmet>
              <title>Track Your Progress</title>
              <meta
                name="keywords"
                content="Pdi, Bengaluru, Bangalore, Kardo, Inspection, Ze, Pre delivery, Services, Car, Cars, Vaidya, BHP, Team, CID, Expert, In, Best, 24, BOSS, Dail, Just, New car, Checklist, Detailed, Report, Inspect, Reliable, Blog, Used car inspection, Buying a new car"
              />
              <meta
                name="description"
                content="Explore our reliable pre-delivery inspection services for new and used cars. Detailed reports, expert insights, and the best service in Bangalore and beyond."
              />
            </Helmet>
      <Navbar />
      <div className="w-full min-h-screen bg-banner bg-cover bg-center  flex flex-col items-center justify-start pb-10 pt-16">
      <div className="flex items-center justify-center py-10 lg:scale-100 scale-50">
      <Stepper
        connectorStateColors={true}
        styleConfig={{
          size: '2em',
          circleFontSize: '1rem',
          connectorStyleConfig: {
            activeColor: '#ADD144',
            completedColor: '#ADD144',
            disabledColor: '#D4D4D4',
            size: 2,
            style: 'solid',
          },
        }}
      >
        {[
          { label: 'Step 1', condition: serviceDetails.status !== 'Pending' },
          { label: 'Step 2', condition: vinNumber },
          { label: 'Step 3', condition: status === 'Vehicle Received' || serviceDetails.status === 'Completed' },
          { label: 'Step 4', condition: serviceDetails.status === 'Completed' },
          { label: 'Step 5', condition: serviceDetails.status === 'Completed' },
        ].map((step, index) => (
          <Step
            key={index}
            label={
              <span className={`text-[18px] font-semibold ${step.condition ? 'text-[#ADD144]' : 'text-[#D4D4D4]'}`}>
                {step.label}
              </span>
            }
            className={`h-8 w-8 rounded-full flex items-center justify-center ${step.condition ? 'bg-[#ADD144] text-[#1F1F1F]' : 'bg-[#1F1F1F] text-[#ADD144]'}`}
            style={{
              backgroundColor: step.condition ? '#ADD144' : '#1F1F1F',
              color: step.condition ? '#1F1F1F' : '#ADD144',
            }}
          />
        ))}
      </Stepper>
      </div>

        {serviceDetails ? (
  <div className="flex font-clash flex-col lg:flex-row items-center justify-center w-full max-w-sm sm:max-w-md md:max-w-2xl lg:max-w-5xl mb-24 bg-[#1F1F1F] border border-[#ADD144] p-4 sm:p-6 rounded-lg shadow-2xl mt-3 space-y-4 lg:space-y-0 overflow-hidden">
    {/* Left Section */}
    <div className="w-full lg:w-2/3 flex flex-col items-center p-4 relative">
      <img
        src={serviceDetails.vehicleImageUrl || car}
        alt="image of car"
        className="w-full max-w-xs sm:max-w-md lg:max-w-full object-contain"
      />

      {/* Stats Container */}
      <div className="flex flex-wrap w-full items-center justify-center gap-4 sm:gap-6 border-t border-l border-r border-[#ADD144] shadow-xl p-4 sm:p-6 rounded-lg mt-4 lg:mt-0">
        {/* Individual Stats */}
        <div className="flex flex-col items-center lg:items-start">
          <p className="text-[#F0FFC5] text-base sm:text-lg lg:text-2xl font-semibold">
          {serviceDetails.bhp || 'N/A'}
          </p>
          <p className="text-[#FFFF] text-sm sm:text-base lg:text-lg">
            BHP
          </p>
        </div>
        <div className="w-[1px] h-6 sm:h-8 lg:h-10 bg-[#ADD144] hidden sm:block"></div>
        <div className="flex flex-col items-center lg:items-start">
          <p className="text-[#F0FFC5] text-base sm:text-lg lg:text-2xl font-semibold">
          {serviceDetails.airbags || 'N/A'}
          </p>
          <p className="text-[#FFFF] text-sm sm:text-base lg:text-lg">
            Airbags
          </p>
        </div>
        <div className="w-[1px] h-6 sm:h-8 lg:h-10 bg-[#ADD144] hidden sm:block"></div>
        <div className="flex flex-col items-center lg:items-start">
          <p className="text-[#F0FFC5] text-base sm:text-lg lg:text-2xl font-semibold">
          {serviceDetails.mileage || 'N/A'}
          </p>
          <p className="text-[#FFFF] text-sm sm:text-base lg:text-lg">
            Mileage (kmpl)
          </p>
        </div>
        <div className="w-[1px] h-6 sm:h-8 lg:h-10 bg-[#ADD144] hidden sm:block"></div>
        <div className="flex flex-col items-center lg:items-start">
          <p className="text-[#F0FFC5] text-base sm:text-lg lg:text-2xl font-semibold">
          {serviceDetails.ncap || 'N/A'}
          </p>
          <p className="text-[#FFFF] text-sm sm:text-base lg:text-lg">
            NCAP
          </p>
        </div>
      </div>
    </div>

    {/* Right Section */}
    <div className="w-full lg:w-1/3 flex flex-col items-start p-2">
      <ul className="p-2 sm:p-4 w-full">
        <li className="relative mb-4 text-[#FFFFFF] text-lg sm:text-xl lg:text-2xl font-semibold pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[linear-gradient(to_bottom_right,_rgba(240,255,197,0),_rgba(240,255,197,0.2))]">
          <span>{serviceDetails.vehicleMake} {serviceDetails.variant} {serviceDetails.vehicleModel}</span>
        </li>
        <li className="py-2 text-[#FFFFFF] text-sm sm:text-base lg:text-lg">
          <span className="font-semibold">Booking ID</span> : {serviceDetails._id}
        </li>
        <li className="text-[#FFFFFF] text-sm sm:text-base lg:text-lg py-2">
          <span className="font-semibold">VIN:</span> {serviceDetails.allottedVin}
        </li>
        <li className="text-[#FFFFFF] text-sm sm:text-base lg:text-lg py-2">
          <span className="font-semibold">Fuel</span> : {serviceDetails.fuel || 'Not specified'}
        </li>
        <li className="text-[#FFFFFF] text-sm sm:text-base lg:text-lg py-2">
          <span className="font-semibold">Engine</span> : {serviceDetails.engine || 'Not specified'}
        </li>
        <li className="text-[#FFFFFF] text-sm sm:text-base lg:text-lg py-2">
          <span className="font-semibold">Variant:</span> {serviceDetails.variant}
        </li>
        <li className="text-[#FFFFFF] text-sm sm:text-base lg:text-lg py-2">
          <span className="font-semibold">Status</span> : {serviceDetails.status}
        </li>
        {serviceDetails?.customMessage && (
          <li className="py-2 text-[#FFFFFF] text-sm sm:text-base lg:text-lg">
            <span className="font-semibold">Message</span> : {serviceDetails?.customMessage}
          </li>
        )}
      </ul>
    </div>
  </div>
) : (
  <p className="text-center text-gray-500">
    No details available for this service.
  </p>
)}


        {/* Forms For user view */}

        {/* Booking */}

        <div className="flex flex-col items-center justify-center font-clash  w-full">
          <div className="flex items-center justify-center w-full px-4 sm:px-0">
            <div className="flex w-full lg:max-w-5xl sm:max-w-md max-w-sm flex-col items-center justify-center space-y-4 border border-gray-300 p-4 sm:p-6 rounded-md relative">
              <div className="w-full text-left pl-3 sm:pl-5">
                <h4 className="text-[#FFFFFF] text-[20px] sm:text-[24px] font-semibold mt-5">
                  <span className="font-normal text-[16px] sm:text-[20px]">
                    Booking ID
                  </span>
                  {" : "}
                  {serviceDetails._id}
                </h4>

                <h4 className="text-[#FFFFFF] text-[20px] sm:text-[24px] pt-4 sm:pt-5 font-semibold">
                  <span className="font-normal text-[16px] sm:text-[20px]">
                    Booking Date
                  </span>
                  {" : "}
                  {new Date(serviceDetails.deliveryDate).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                </h4>
              </div>

              <div className={`absolute h-8 w-8 sm:h-10 sm:w-10 rounded-full left-[-15px] sm:left-[-20px] top-8 sm:top-10 flex items-center justify-center ${
                  serviceDetails.status === "Pending" ? "bg-[#1F1F1F] text-[#ADD144]" : "bg-[#ADD144] text-[#1F1F1F]"
                }  
                `}>
                <p className="text-[16px] sm:text-[20px]">1</p>
              </div>
              <div
                className={`px-8 sm:px-12 py-1 sm:py-2 text-[#FFFFFF] absolute top-[-30px] sm:top-[-38px] right-4 sm:right-10 rounded-full ${
                  serviceDetails.status === "Pending" ? "bg-[#FFBD6F] text-black" : "bg-[#0B7300] text-white"
                }`}
              >
                {serviceDetails.status === "Pending" ? "PENDING" : "COMPLETED"}
              </div>
            </div>
          </div>

          <div className="">
            <img src={arrow} alt="image of arrow" className="" />
          </div>

          {/* VIN Allocation */}

          <div className="flex items-center justify-center w-full px-4 sm:px-0">
            <div className="flex w-full lg:max-w-5xl sm:max-w-md max-w-sm flex-col items-center justify-center space-y-4 border border-gray-300 p-4 sm:p-6 rounded-md relative">
              <div className="w-full text-left pl-3 sm:pl-5  ">
                <h4 className="text-[#FFFFFF] text-[20px] sm:text-[24px] mt-5">
                  VIN allocation by dealer
                </h4>

                <h4 className="text-[#FFFFFF] text-[16px] sm:text-[18px] pt-4 sm:pt-5 font-semibold">
                  My car dealer has allocated vehicle identification number
                </h4>
              </div>

              <div className="flex flex-col justify-start pl-3 sm:pl-5 pt-4 sm:pt-6 w-full">
                <div className="flex flex-wrap items-left justify-start space-x-0 sm:space-x-4 w-full sm:w-[700px]">
                  <div className="mb-8 w-full sm:w-[400px]">
                    <div className="relative w-full">
                    <input
                      type="text"
                      name="SensorCode"
                      value={vinNumber}
                      onChange={handleChange}
                      required
                      readOnly={!isEditable}
                      className={`border rounded-full w-full py-4 sm:py-6 px-4 sm:px-5 pr-16 sm:pr-20 bg-[#1F1F1F] border-none text-[#F0FFC5] ${
                        !isEditable ? "cursor-not-allowed" : ""
                      }`}
                    />
                    <div
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 border border-[#ADD144] rounded-full px-8 sm:px-12 py-2 sm:py-3 text-[#F0FFC5] bg-banner bg-opacity-70 cursor-pointer"
                      onClick={() => (isEditable ? handleSaveVIN() : setIsEditable(true))}
                    >
                      {vinNumber ? (isEditable ? "Save" : "Edit") : "Add"}
                    </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full text-left pl-3 sm:pl-5">
                <h4 className="text-[#FFFFFF] text-[14px] sm:text-[18px] font-semibold">
                  Note : how many characters vin
                </h4>
              </div>

              <div className={`absolute h-8 w-8 sm:h-10 sm:w-10 rounded-full left-[-15px] sm:left-[-20px] top-8 sm:top-10 flex items-center justify-center ${
                  vinNumber ? "bg-[#ADD144] text-[#1F1F1F]" : "bg-[#1F1F1F] text-[#ADD144]"
                }`}>
                <p className="text-[16px] sm:text-[20px]">2</p>
              </div>
              <div
                className={`px-8 sm:px-12 py-1 sm:py-2 text-[#FFFFFF] absolute top-[-30px] sm:top-[-38px] right-4 sm:right-10 rounded-full ${
                  vinNumber ? "bg-[#0B7300] text-white" : "bg-[#FFBD6F] text-black"
                }`}
              >
                {vinNumber ? "COMPLETED" : "PENDING"}
              </div>
            </div>
          </div>

          <div className="">
            <img src={arrow} alt="image of arrow" className="" />
          </div>

          {/* Confirmation */}

          <div className="flex items-center justify-center w-full px-4 sm:px-0">
            <div className="flex w-full lg:max-w-5xl sm:max-w-md max-w-sm flex-col items-center justify-center space-y-4 border border-gray-300 p-4 sm:p-6 rounded-md relative">
              <div className="w-full text-left">
                <h4 className="text-[#FFFFFF] text-[20px] sm:text-[24px] mt-5">
                  Confirmation : Vehicle received by my dealer
                </h4>
                <div className="flex items-start sm:items-center mt-4">
                <input
                  type="checkbox"
                  id="confirm-arrival"
                  className="mr-2 mt-1 sm:mt-0"
                  checked={isChecked || serviceDetails.status === "Completed"}
                  onChange={handleCheckboxChange}
                  disabled={status === "Vehicle Received" || serviceDetails.status === "Completed"}
                />
                  <label
                    htmlFor="confirm-arrival"
                    className="text-[#FFFFFF] text-[16px] sm:text-[18px]"
                  >
                    My car dealer has received vehicle from the plant.
                  </label>
                </div>
              </div>
              <div className="w-full flex justify-center">
                <button className="text-[#F0FFC5] text-sm sm:text-md font-semibold py-3 sm:py-4 px-8 sm:px-12 bg-[#1F1F1F] rounded-3xl shadow-inner-custom"
                 onClick={handleConfirmClick}
                 disabled={!isChecked || status === "Vehicle Received" || serviceDetails.status === "Completed"}
                 type="button"                 
                >
                  {status === "Vehicle Received" || serviceDetails.status === "Completed" ? "Confirmed" : "Confirm"}
                </button>
              </div>
              <div className={`absolute h-8 w-8 sm:h-10 sm:w-10 rounded-full left-[-15px] sm:left-[-20px] top-8 sm:top-10 flex items-center justify-center ${
                  status === "Vehicle Received" || serviceDetails.status === "Completed" ? "bg-[#ADD144] text-[#1F1F1F]" : "bg-[#1F1F1F] text-[#ADD144]"
                }`}>
                <p className="text-[16px] sm:text-[20px]">3</p>
              </div>
              <div
                className={`px-8 sm:px-12 py-1 sm:py-2 absolute top-[-30px] sm:top-[-38px] right-4 sm:right-10 rounded-full ${
                  status === "Vehicle Received" || serviceDetails.status === "Completed" ? "bg-[#0B7300] text-white" : "bg-[#FFBD6F] text-black"
                }`}
              >
                {status === "Vehicle Received" || serviceDetails.status === "Completed" ? "COMPLETED" : "PENDING"}
              </div>
            </div>
          </div>

          <div className="">
            <img src={arrow} alt="image of arrow" className="" />
          </div>

          {/* Confirm PDI Inspection */}

          <div className="flex items-center justify-center w-full px-4 sm:px-0">
            <div className="flex w-full lg:max-w-5xl sm:max-w-md max-w-sm flex-col items-center justify-center space-y-4 border border-gray-300 p-4 sm:p-6 rounded-md relative">
              <div className="w-full text-left">
                <h4 className="text-[#FFFFFF] text-[20px] sm:text-[24px] pl-3 sm:pl-5">
                  Confirm PDI Inspection
                </h4>
                <h4 className="text-[#FFFFFF] text-[16px] sm:text-[18px] pt-4 sm:pt-5 font-semibold pl-3 sm:pl-5">
                  My car dealer has allocated vehicle identification number
                </h4>
              </div>

              <div className="flex flex-col sm:flex-row items-start justify-start gap-3 w-full pl-3 sm:pl-5 pt-3">
                <button
                  onClick={() => {
                    setDealerType((prev) =>
                      prev === "Showroom" ? null : "Showroom"
                    );
                  }}
                  className={`flex items-center justify-center space-x-4 py-3 px-6 rounded-full transition-all duration-300 ${
                    dealerType === "Showroom"
                      ? "bg-[#191F09] text-[#ADD144] font-semibold"
                      : "bg-transparent border border-white text-white font-semibold"
                  }`}
                  disabled={!isTimeEditable}
                >
                  <span
                    className={`relative flex items-center justify-center w-5 h-5 border-2 border-white rounded-full transition-colors duration-300 ${
                      dealerType === "Showroom"
                        ? "bg-[#ADD144]"
                        : "bg-transparent"
                    }`}
                  >
                    {dealerType === "Showroom" && (
                      <span className="absolute text-[#191F09] text-lg">
                        &#10003;
                      </span>
                    )}
                  </span>
                  <span>Dealer Showroom</span>
                </button>

                <button
                  onClick={() => {
                    setDealerType((prev) =>
                      prev === "Stockyard" ? null : "Stockyard"
                    );
                  }}
                  className={`flex items-center justify-center space-x-4 py-3 px-6 rounded-full transition-all duration-300 ${
                    dealerType === "Stockyard"
                      ? "bg-[#191F09] text-[#ADD144] font-semibold"
                      : "bg-transparent border border-white text-white font-semibold"
                  }`}
                  disabled={!isTimeEditable}
                >
                  <span
                    className={`relative flex items-center justify-center w-5 h-5 border-2 border-white rounded-full transition-colors duration-300 ${
                      dealerType === "Stockyard"
                        ? "bg-[#ADD144]"
                        : "bg-transparent"
                    }`}
                  >
                    {dealerType === "Stockyard" && (
                      <span className="absolute text-[#191F09] text-lg">
                        &#10003;
                      </span>
                    )}
                  </span>
                  <span>Stockyard</span>
                </button>
              </div>

              <div className="flex flex-col items-start justify-start gap-3 w-full pl-3 sm:pl-5 pt-3">
                <div className="relative mb-6 w-full sm:w-[300px]">
                  <div className="flex items-center border rounded-full py-4 px-4 bg-[#1F1F1F] border-[#F0FFC5] focus-within:ring-2 focus-within:ring-[#F0FFC5] transition text-[#F0FFC5] relative">
                    <FaCalendarAlt className="text-[#F0FFC5] mr-3" />
                    <DatePicker
                      selected={selectedDate}
                      onChange={(date) => setSelectedDate(date)}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select a date"
                      className="bg-transparent w-full outline-none text-[#F0FFC5] placeholder-[#F0FFC5]"
                      showPopperArrow={false}
                      disabled={!isTimeEditable}
                    />
                    {selectedDate && (
                      <button
                        type="button"
                        onClick={() => setSelectedDate(null)}
                        className="text-[#F0FFC5] ml-2 focus:outline-none"
                        disabled={!isTimeEditable}
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>

                <div className="relative mb-6 w-full sm:w-[500px]">
                  <input
                    type="text"
                    placeholder="Address"
                    name="Address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    required
                    className="border rounded-full w-full py-4 px-5 bg-[#1F1F1F] border-[#F0FFC5] text-[#F0FFC5]"
                    disabled={!isTimeEditable}
                  />
                </div>
              </div>

              <div className="w-full flex lg:flex-row flex-col justify-center gap-5 pt-4">
                <button className="text-[#F0FFC5] text-sm sm:text-md font-semibold py-3 sm:py-4 px-10 sm:px-12 bg-[#1F1F1F] rounded-3xl shadow-inner-custom"
                onClick={handleSave}
                disabled={!isTimeEditable}
                >
                  {!isTimeEditable ? "Reschedule window closed" : "Reschedule"}
                </button>

                <button className="text-[#F0FFC5] text-sm sm:text-md font-semibold py-3 sm:py-4 px-10 sm:px-12 bg-[#1F1F1F] rounded-3xl shadow-inner-custom"
                >
                 Contact Customer Care
                </button>


                
              </div>

              <div className={`absolute h-8 sm:h-10 w-8 sm:w-10 rounded-full left-[-15px] sm:left-[-20px] top-40 sm:top-52 flex items-center justify-center ${
                  serviceDetails.status !== "Completed" ? "bg-[#1F1F1F] text-[#ADD144]" : "bg-[#ADD144] text-[#1F1F1F]"
                }  
                `}>
                <p className="text-[16px] sm:text-[20px]">4</p>
              </div>
              <div
                className={`px-8 sm:px-12 py-1 sm:py-2 text-white absolute top-[-30px] sm:top-[-38px] right-4 sm:right-10 rounded-full ${
                  serviceDetails.status !== "Completed" ? "bg-[#FFBD6F] text-black" : "bg-[#0B7300] text-white"
                }`}
              >
                {serviceDetails.status !== "Completed" ? "PENDING" : "COMPLETED"}
              </div>
            </div>
          </div>

          <div className="">
            <img src={arrow} alt="image of arrow" className="" />
          </div>

          {/* Generate PDF */}

          <div className="flex items-center justify-center w-full px-4 pb-32">
            <div className="flex w-full sm:max-w-md max-w-sm lg:max-w-5xl flex-col items-center justify-center space-y-4 border border-gray-300 p-6 rounded-md relative bg-[#2A2A2A]">
              {/* Header Section */}
              <div className="w-full text-left sm:text-center lg:text-left">
                <h4 className="text-[#FFFFFF] text-lg sm:text-xl lg:text-2xl">
                  Generate PDI Report
                </h4>
              </div>

              {/* Button Section */}
              <div className="w-full flex justify-center">
                <button className="text-[#F0FFC5] text-sm sm:text-md lg:text-lg font-semibold py-3 sm:py-4 px-8 sm:px-12 bg-[#1F1F1F] rounded-3xl shadow-inner-custom hover:bg-[#333333] transition"
                onClick={handleGeneratePDIReport}
                >
                  PDI Report
                </button>
              </div>

              {/* Step Number Circle */}
              <div className={`absolute h-8 w-8 sm:h-10 sm:w-10 rounded-full left-[-10px] sm:left-[-20px] top-12 sm:top-16 flex items-center justify-center ${
                  serviceDetails.status !== "Completed" ? "bg-[#1F1F1F] text-[#ADD144]" : "bg-[#ADD144] text-[#1F1F1F]"
                }  
                `}>
                <p className="text-[14px] sm:text-[16px] lg:text-[20px]">
                  5
                </p>
              </div>

              {/* Pending Status */}
              <div className={`px-6 sm:px-8 py-1 sm:py-2 text-xs sm:text-sm lg:text-md text-black absolute top-[-20px] sm:top-[-38px] right-5 sm:right-10 rounded-full ${
                  serviceDetails.status !== "Completed" ? "bg-[#FFBD6F] text-black" : "bg-[#0B7300] text-white"
                }`}>
                {serviceDetails.status !== "Completed" ? "PENDING" : "COMPLETED"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default TrackProgress;
